// Aggregazione spaziale dei dati ambientali UGO di piu' sessioni sul percorso di gruppo (tab
// "Qualita' dell'aria di gruppo" di un'iniziativa). Funzioni pure, senza React.
//
// Il percorso di gruppo arriva dal backend gia' diviso in pezzi (`pieces`, ognuno
// `{ estimated, points: [{ lat, lng }] }`): l'unione dei tracciati di tutti i partecipanti, piu' i
// tratti stimati dei buchi GPS (`estimated`). I pezzi non si uniscono mai fra loro: solo i raccordi corti (buildJoins) chiudono lo
// stacco dove un pezzo si stacca da un altro.
//
// Per ogni punto di un pezzo si prendono i campioni di tutti i partecipanti entro un raggio in
// metri, si scartano i fuori scala rispetto al gruppo e si fa la media. Dove non ci sono campioni
// il colore sfuma fra i punti con dati dello stesso pezzo, solo entro MAX_COLOR_DISTANCE_M lungo
// il pezzo: oltre la linea e' grigia, perche' li' nessuno ha misurato quella grandezza.
//
// Si scarta un campione per il suo valore, quando la sua posizione non e' nota (buco GPS senza
// stima, `positionUncertain` dal backend) o quando e' stato preso in sosta (`stationary`, solo in
// dashboard: gli Open Data non mandano i campioni in sosta). Le soste si tolgono per lo stato
// della bici ferma, non per l'orario (decisione del 03/11/2026, da confermare col committente):
// resta valida la scelta del committente del 26/10/2026 di non scartare mai un campione per il
// momento in cui e' stato preso. Un campione lontano da ogni pezzo non e' scartato: conta come
// usato anche se non colora nessuna linea.
//
// Numeri di produzione dietro alle costanti (26/10/2026): un campione ogni ~98 s, cioe' ogni
// ~194 m in mediana (p90 492 m); partial GPS ogni 10-18 m; massimi osservati AQI 199, ozono
// 637 ppb, PM ~135 µg/m³ con un tetto sintetico del sensore a 2000; dispersione intra-sessione
// |v - mediana| / mediana: p95 1,0, p99 3,3.

import { NO_DATA_COLOR, getMetricColor, hexToRgb, lerpRgb, normalizeMetricValue } from "../constants/airQuality";

/** Raggio dell'intorno attorno a ogni punto del percorso. */
export const NEIGHBOURHOOD_RADIUS_M = 150;

/** Distanza massima fra il capo di un pezzo e il punto piu' vicino di un altro pezzo per disegnare un raccordo. */
export const JOIN_RADIUS_M = 300;

/**
 * Oltre questa distanza lungo il pezzo dal punto con dati piu' vicino la linea e' grigia.
 * Punto aperto della spec del 03/11/2026: valore proposto, da confermare.
 */
export const MAX_COLOR_DISTANCE_M = 1000;

/** Oltre questi valori un campione e' fisicamente implausibile e non entra in nessuna media. */
export const PLAUSIBILITY_MAX = {
    aqi_epa: 500, // l'AQI EPA e' definito fino a 500: vale sul valore gia' raddoppiato
    ozone_density: 1000, // ppb
    pm1: 1000, // µg/m³
    pm2_5: 1000,
    pm10: 1000,
};

/** Fuori scala di gruppo: oltre OUTLIER_RATIO volte la mediana dell'intorno, o sotto 1/OUTLIER_RATIO. */
export const OUTLIER_RATIO = 3;

/** Il fuori scala si applica solo con almeno questi candidati nell'intorno. */
export const MIN_OUTLIER_CANDIDATES = 3;

/**
 * Per ogni metrica, da quale serie si legge e con quale campo.
 * `aqi_epa` legge `aqiEpa` come fanno gli Open Data.
 */
export const METRIC_SOURCE = {
    ozone_density: { series: "oaq", field: "ozoneDensity" },
    aqi_epa: { series: "oaq", field: "aqiEpa" },
    pm1: { series: "pmx", field: "pm1Density" },
    pm2_5: { series: "pmx", field: "pm2Dot5Density" },
    pm10: { series: "pmx", field: "pm10Density" },
};

const EARTH_RADIUS_M = 6371000;
const METERS_PER_DEGREE_LAT = 111000;

/** Distanza in metri fra due punti {lat, lng}. */
export function haversineM(a, b) {
    const toRad = d => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s =
        Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(s));
}

/**
 * Griglia spaziale sugli elementi {lat, lng}: celle larghe almeno `radiusM`, ricerca nelle 3x3
 * celle intorno. Serve a non confrontare ogni punto con ogni campione (decine di migliaia).
 */
function buildGrid(items, radiusM) {
    const cellLat = radiusM / METERS_PER_DEGREE_LAT;
    const maxAbsLat = items.reduce((m, it) => Math.max(m, Math.abs(it.lat)), 0);
    const cellLng = cellLat / Math.cos((Math.min(89, maxAbsLat + 0.01) * Math.PI) / 180);
    const cells = new Map();
    const key = (i, j) => `${i}|${j}`;
    items.forEach(it => {
        const k = key(Math.floor(it.lat / cellLat), Math.floor(it.lng / cellLng));
        if (!cells.has(k)) cells.set(k, []);
        cells.get(k).push(it);
    });
    return {
        /** Gli elementi entro `radiusM` da `p`. */
        near(p) {
            const i = Math.floor(p.lat / cellLat);
            const j = Math.floor(p.lng / cellLng);
            const out = [];
            for (let a = -1; a <= 1; a++) {
                for (let b = -1; b <= 1; b++) {
                    (cells.get(key(i + a, j + b)) || []).forEach(it => {
                        if (haversineM(p, it) <= radiusM) out.push(it);
                    });
                }
            }
            return out;
        },
    };
}

/**
 * Campioni di una metrica, con il valore normalizzato (ozono /100, AQI x2) e quello grezzo
 * (`raw`), solo dalla serie giusta; valori nulli o <= 0 scartati (lo zero e' il sensore non pronto,
 * come negli Open Data). `originalLat`/`originalLng` sono le coordinate della risposta, che restano
 * anche quando il campione prende la posizione stimata.
 */
export function extractMetricSamples(samples, metricKey) {
    const source = METRIC_SOURCE[metricKey];
    if (!source) return [];
    return (samples || [])
        .filter(s => s.series === source.series)
        .map(s => ({
            sessionId: s.sessionId,
            timestamp: s.timestamp,
            lat: s.lat,
            lng: s.lng,
            originalLat: s.lat,
            originalLng: s.lng,
            value: normalizeMetricValue(metricKey, s[source.field]),
            raw: s[source.field],
            positionUncertain: !!s.positionUncertain,
            stationary: !!s.stationary,
            estimatedLat: s.estimatedLat ?? null,
            estimatedLng: s.estimatedLng ?? null,
        }))
        .filter(s => s.value != null && s.value > 0 && s.lat != null && s.lng != null);
}

function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Aggrega i campioni sui pezzi del percorso di gruppo per la metrica scelta. I campioni a
 * posizione incerta con una stima (`estimatedLat`/`estimatedLng`) prendono la posizione stimata
 * (`estimated`) e contano come gli altri.
 *
 * @returns {{ pieces: [{ estimated, points: [{lat,lng,value,color}] }], samples: [{...sample, rejected?, estimated?}], usedCount, hasData }}
 *   `rejected` e' assente per i campioni usati, altrimenti "stationary" (preso in sosta),
 *   "uncertainPosition" (preso durante un buco GPS, senza stima), "implausible" (oltre il tetto)
 *   o "outlier" (escluso da tutti gli intorni in cui era candidato).
 */
export function aggregate(pieces, samples, metricKey, options = {}) {
    const radiusM = options.radiusM ?? NEIGHBOURHOOD_RADIUS_M;
    const outlierRatio = options.outlierRatio ?? OUTLIER_RATIO;
    const minOutlierCandidates = options.minOutlierCandidates ?? MIN_OUTLIER_CANDIDATES;
    const plausibilityMax = options.plausibilityMax ?? PLAUSIBILITY_MAX;
    const maxColorDistanceM = options.maxColorDistanceM ?? MAX_COLOR_DISTANCE_M;

    const metricSamples = extractMetricSamples(samples, metricKey);
    const maxValue = plausibilityMax[metricKey];
    metricSamples.forEach(s => {
        if (s.stationary) {
            s.rejected = "stationary";
            return;
        }
        if (s.positionUncertain) {
            if (s.estimatedLat == null || s.estimatedLng == null) {
                s.rejected = "uncertainPosition";
                return;
            }
            s.lat = s.estimatedLat;
            s.lng = s.estimatedLng;
            s.estimated = true;
        }
        if (maxValue != null && s.value > maxValue) s.rejected = "implausible";
    });
    const candidates = metricSamples.filter(s => !s.rejected);
    const grid = buildGrid(candidates, radiusM);

    // Per ogni campione: in quanti intorni e' stato candidato e in quanti e' stato escluso.
    const candidateIn = new Map();
    const excludedIn = new Map();

    const valueAt = p => {
        const near = grid.near(p);
        if (near.length === 0) return null;
        near.forEach(s => candidateIn.set(s, (candidateIn.get(s) || 0) + 1));
        let kept = near;
        if (near.length >= minOutlierCandidates) {
            const med = median(near.map(s => s.value));
            kept = near.filter(s => s.value <= med * outlierRatio && s.value >= med / outlierRatio);
            near.filter(s => !kept.includes(s)).forEach(s => excludedIn.set(s, (excludedIn.get(s) || 0) + 1));
        }
        if (kept.length === 0) return null;
        return kept.reduce((sum, s) => sum + s.value, 0) / kept.length;
    };

    const out = (pieces || []).map(piece => {
        const values = piece.points.map(valueAt);
        const colors = colorsAlongPiece(piece.points, values, metricKey, maxColorDistanceM);
        return {
            estimated: !!piece.estimated,
            points: piece.points.map((p, i) => ({ lat: p.lat, lng: p.lng, value: values[i], color: colors[i] })),
        };
    });

    candidates.forEach(s => {
        const c = candidateIn.get(s) || 0;
        if (c > 0 && (excludedIn.get(s) || 0) === c) s.rejected = "outlier";
    });

    const usedCount = metricSamples.filter(s => !s.rejected).length;
    const hasData = out.some(piece => piece.points.some(p => p.value != null));
    return { pieces: out, samples: metricSamples, usedCount, hasData };
}

/** Colore di un punto prima del primo punto con dati o dopo l'ultimo: quello del piu' vicino. */
function edgeColor(anchor, distanceM, { colored, grey, maxDistanceM }) {
    return distanceM <= maxDistanceM ? colored[anchor] : grey;
}

/** Colore di un punto fra due punti con dati: sfumatura in proporzione alla distanza lungo il pezzo. */
function blendedColor(i, prev, next, { colored, dist, grey, maxDistanceM }) {
    const fromPrev = dist[i] - dist[prev];
    const toNext = dist[next] - dist[i];
    if (Math.min(fromPrev, toNext) > maxDistanceM) return grey;
    const span = dist[next] - dist[prev];
    return lerpRgb(colored[prev], colored[next], span > 0 ? fromPrev / span : 0);
}

/**
 * Colore di ogni punto di un pezzo: la fascia del valore dove c'e'; altrimenti sfumatura fra il
 * punto con dati precedente e il successivo in proporzione alla distanza lungo il pezzo; prima
 * del primo o dopo l'ultimo, il colore del piu' vicino. Sempre grigio se il punto con dati piu'
 * vicino dista lungo il pezzo piu' di `maxDistanceM`, e in un pezzo senza dati.
 */
function colorsAlongPiece(points, values, metricKey, maxDistanceM) {
    const n = points.length;
    const grey = hexToRgb(NO_DATA_COLOR);
    const colored = values.map(v => (v == null ? null : hexToRgb(getMetricColor(metricKey, v))));
    const anchors = [];
    colored.forEach((c, i) => {
        if (c) anchors.push(i);
    });
    if (anchors.length === 0) return points.map(() => grey);

    const dist = new Array(n).fill(0);
    for (let i = 1; i < n; i++) dist[i] = dist[i - 1] + haversineM(points[i - 1], points[i]);

    const line = { colored, dist, grey, maxDistanceM };
    const first = anchors[0];
    const last = anchors[anchors.length - 1];
    const out = new Array(n);
    let a = 0; // indice in anchors dell'ultima ancora fino a i
    for (let i = 0; i < n; i++) {
        while (a < anchors.length - 1 && anchors[a + 1] <= i) a++;
        if (colored[i]) out[i] = colored[i];
        else if (i < first) out[i] = edgeColor(first, dist[first] - dist[i], line);
        else if (i > last) out[i] = edgeColor(last, dist[i] - dist[last], line);
        else out[i] = blendedColor(i, anchors[a], anchors[a + 1], line);
    }
    return out;
}

/**
 * Segmenti per l'overlay: uno per coppia di punti consecutivi dello stesso pezzo, col colore del
 * primo, mai fra due pezzi. `pieces` sono quelli di `aggregate`.
 */
export function buildSegments(pieces) {
    const out = [];
    (pieces || []).forEach(({ estimated, points }) => {
        for (let i = 0; i < points.length - 1; i++) {
            const a = points[i];
            const b = points[i + 1];
            out.push({ path: [[a.lng, a.lat], [b.lng, b.lat]], color: a.color, estimated: !!estimated });
        }
    });
    return out;
}

/**
 * Raccordi fra i pezzi: per ogni capo di ogni pezzo, un segmento fino al punto piu' vicino di un
 * altro pezzo, se dista al massimo `radiusM`, col colore del capo. Chiudono lo stacco fino a 150 m
 * che resta dove un pezzo aggiunto si stacca da quelli gia' presi. Mai un raccordo con se stesso;
 * due capi che si raccordano a vicenda danno un raccordo solo. Un raccordo che tocca un pezzo
 * stimato e' anch'esso stimato (tratteggiato): non deve sembrare un tratto rilevato.
 */
export function buildJoins(pieces, radiusM = JOIN_RADIUS_M) {
    const all = [];
    (pieces || []).forEach((piece, index) =>
        piece.points.forEach(p => all.push({ lat: p.lat, lng: p.lng, index, estimated: !!piece.estimated }))
    );
    if (all.length === 0) return [];
    const grid = buildGrid(all, radiusM);
    const out = [];
    const seen = new Set();
    pieces.forEach(({ estimated, points }, index) => {
        if (points.length === 0) return;
        const ends = points.length === 1 ? [points[0]] : [points[0], points[points.length - 1]];
        ends.forEach(end => {
            let best = null;
            let bestM = Infinity;
            grid.near(end).forEach(q => {
                if (q.index === index) return;
                const m = haversineM(end, q);
                if (m < bestM) {
                    best = q;
                    bestM = m;
                }
            });
            if (!best) return;
            const a = `${end.lng},${end.lat}`;
            const b = `${best.lng},${best.lat}`;
            const key = a < b ? `${a}|${b}` : `${b}|${a}`;
            if (seen.has(key)) return;
            seen.add(key);
            out.push({
                path: [[end.lng, end.lat], [best.lng, best.lat]],
                color: end.color,
                estimated: !!(estimated || best.estimated),
            });
        });
    });
    return out;
}

/**
 * Percorsi continui dei tratti stimati: i segmenti `estimated` consecutivi (uno finisce dove inizia
 * il successivo) uniti in un solo percorso `{ path: [[lng, lat], ...] }`, cosi' il tratteggio
 * dell'overlay resta regolare anche su segmenti di pochi metri.
 */
export function estimatedRuns(segments) {
    const runs = [];
    let current = null;
    (segments || []).forEach(s => {
        if (!s.estimated) {
            current = null;
            return;
        }
        const [a, b] = s.path;
        const last = current && current.path[current.path.length - 1];
        if (last && last[0] === a[0] && last[1] === a[1]) {
            current.path.push(b);
        } else {
            current = { path: [a, b] };
            runs.push(current);
        }
    });
    return runs;
}
